/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useUI } from "../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import { getStoreDisplayName } from "../utils/getStoreDisplayName";
import { navigateWithExpiredPlanGuard } from "../utils/expiredPlanAccess";
import clsx from "clsx";
import {
  FiHome,
  FiShoppingCart,
  FiBox,
  FiUsers,
  FiSettings,
  FiTag,
  FiFileText,
  FiUser,
  FiChevronRight,
  FiChevronDown,
  FiLayers,
  FiBriefcase,
  FiMessageSquare,
  FiBarChart2,
  FiTrendingUp,
  FiPackage,
  FiDollarSign,
  FiExternalLink,
} from "react-icons/fi";

export default function Sidebar({ isOpen, onClose, isAlwaysVisible }) {
  const { lang, theme } = useUI();
  const { canAccessPage } = useTenantStorePermissions();
  const [openMenu, setOpenMenu] = useState("");
  const [storeName, setStoreName] = useState("My Store");
  const navigate = useNavigate();
  const location = useLocation();
  const isArabic = lang === "ar";
  const isDark = theme === "dark";

  const accent = "#7400BF"; // البنفسجي الأساسي
  const softAccent = "#F2B441"; // الذهبي

  const allMenuItems = useMemo(() => [
    {
      key: "dashboard",
      label: isArabic ? "الرئيسية" : "Dashboard",
      icon: <FiHome />,
      to: "/admin-store/dashboard",
      pageKey: null, // دايمًا ظاهر
    },
    {
      key: "orders",
      label: isArabic ? "الطلبات" : "Orders",
      icon: <FiShoppingCart />,
      pageKey: "viewOrders",
      sub: [
        {
          key: "allOrders",
          label: isArabic ? "كل الطلبات" : "All Orders",
          to: "/admin-store/orders",
          pageKey: "viewOrders",
        },
        {
          key: "manualOrders",
          label: isArabic ? "الطلبات اليدوية" : "Manual Orders",
          to: "/admin-store/manual-orders",
          pageKey: "viewOrders",
        },
      ],
    },
    {
      key: "products",
      label: isArabic ? "المنتجات" : "Products",
      icon: <FiBox />,
      pageKey: "viewProducts",
      sub: [
        {
          key: "allProducts",
          label: isArabic ? "كل المنتجات" : "All Products",
          to: "/admin-store/products",
          pageKey: "viewProducts",
        },
        {
          key: "stock",
          label: isArabic ? "المخزون" : "Stock",
          to: "/admin-store/stock",
          pageKey: "viewStock",
        },
        {
          key: "divisions",
          label: isArabic ? "تقسيمات المتجر" : "Store Divisions",
          to: "/admin-store/divisions",
          pageKey: "viewDivisions",
        },
        {
          key: "categories",
          label: isArabic ? "البراندات" : "Brands",
          to: "/admin-store/categories",
          pageKey: "viewCategories",
        },
      ],
    },
    {
      key: "customers",
      label: isArabic ? "الزبائن" : "Customers",
      icon: <FiUsers />,
      to: "/admin-store/customers",
      pageKey: "viewCustomers",
    },
    {
      key: "store",
      label: isArabic ? "المتجر" : "Store",
      icon: <FiLayers />,
      pageKey: "viewBanners",
      sub: [
        {
          key: "themes",
          label: isArabic ? "الثيم واللافتات" : "Theme & Banners",
          to: "/admin-store/store/theme",
          pageKeys: ["viewTheme", "viewBanners"],
        },
        {
          key: "settings",
          label: isArabic ? "إعدادات المتجر" : "Store Settings",
          to: "/admin-store/store/settings",
          pageKey: "viewStoreSettings",
        },
        {
          key: "invoiceSettings",
          label: isArabic ? "إعدادات الفاتورة" : "Invoice Settings",
          to: "/admin-store/settings/invoice",
          pageKey: "viewInvoice",
        },
      ],
    },
    {
      key: "marketing",
      label: isArabic ? "التسويق" : "Marketing",
      icon: <FiTag />,
      pageKey: "viewCoupons",
      sub: [
        {
          key: "coupons",
          label: isArabic ? "كوبونات الخصم" : "Discount Coupons",
          to: "/admin-store/marketing/coupons",
          pageKey: "viewCoupons",
        },
      ],
    },
    {
      key: "reports",
      label: isArabic ? "التقارير" : "Reports",
      icon: <FiBarChart2 />,
      pageKey: "viewAnalytics",
      sub: [
        {
          key: "analytics",
          label: isArabic ? "التحليلات" : "Analytics",
          to: "/admin-store/reports/analytics",
          pageKey: "viewAnalytics",
        },
        {
          key: "salesReport",
          label: isArabic ? "تقرير الارباح" : "Profit Report",
          to: "/admin-store/reports/sales",
          pageKey: "viewSalesReport",
        },
        {
          key: "inventoryReport",
          label: isArabic ? "تقرير المخزون" : "Inventory Report",
          to: "/admin-store/reports/inventory",
          pageKey: "viewInventoryReport",
        },
        {
          key: "customerReport",
          label: isArabic ? "تقرير العملاء" : "Customer Report",
          to: "/admin-store/reports/customers",
          pageKey: "viewCustomerReport",
        },
        {
          key: "productReport",
          label: isArabic ? "تقرير المنتجات" : "Product Report",
          to: "/admin-store/reports/products",
          pageKey: "viewProductReport",
        },
        {
          key: "reportsOrders",
          label: isArabic ? "تقارير الطلبات" : "Order Reports",
          to: "/admin-store/reports/orders",
          pageKey: "viewSalesReport",
        },
      ],
    },
    {
      key: "activity",
      label: isArabic ? "سجل الإجراءات" : "Activity Log",
      icon: <FiFileText />,
      to: "/admin-store/activity",
      pageKey: "viewActivity",
    },
    {
      key: "notifications",
      label: isArabic ? "إضافة رأي/إشعار" : "Add Review/Notification",
      icon: <FiMessageSquare />,
      to: "/admin-store/notifications",
      pageKey: "viewNotification",
    },
    {
      key: "employees",
      label: isArabic ? "الموظفين" : "Employees",
      icon: <FiUser />,
      to: "/admin-store/employees",
      pageKey: "viewEmployees",
    },
    {
      key: "subscriptions",
      label: isArabic ? "الاشتراكات" : "Subscriptions",
      icon: <FiBriefcase />,
      to: "/admin-store/subscriptions",
      pageKey: "viewSubscriptions",
    },
    {
      key: "delivery",
      label: isArabic ? "شركات التوصيل" : "Delivery Companies",
      icon: <FiPackage />,
      to: "/admin-store/settings/shipping",
      pageKey: "viewShipping",
    },
  ], [isArabic]);

  useEffect(() => {
    document.documentElement.dir = isArabic ? "rtl" : "ltr";
  }, [isArabic]);

  // Auto-open menu based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    const parent = allMenuItems.find(item =>
      item.sub && item.sub.some(sub => sub.to === currentPath)
    );
    if (parent) {
      setOpenMenu(parent.key);
    }
  }, [location.pathname, allMenuItems]);

  useEffect(() => {
    const syncStoreName = () => setStoreName(getStoreDisplayName(isArabic));
    syncStoreName();
    const interval = setInterval(syncStoreName, 1000);
    return () => clearInterval(interval);
  }, [isArabic]);

  // تصفية القائمة بناءً على الصلاحيات
  const menu = useMemo(() => {
    return allMenuItems
      .map(item => {
        if (item.sub) {
          const filteredSub = item.sub.filter((sub) =>
            Array.isArray(sub.pageKeys) && sub.pageKeys.length > 0
              ? sub.pageKeys.some((key) => canAccessPage(key))
              : canAccessPage(sub.pageKey)
          );
          if (filteredSub.length > 0) {
            return { ...item, sub: filteredSub };
          }
          return canAccessPage(item.pageKey) ? item : null;
        }
        return canAccessPage(item.pageKey) ? item : null;
      })
      .filter(item => item !== null);
  }, [allMenuItems, canAccessPage]);

  return (
    <aside
      dir={isArabic ? "rtl" : "ltr"}
      className={clsx(
        "h-screen flex flex-col fixed shadow-2xl transition-all duration-500 ease-in-out border-r",
        isArabic ? "right-0" : "left-0",
        isDark
          ? "bg-[#0F172A] border-[#1E293B]"
          : "bg-white border-gray-200",
        isAlwaysVisible
          ? "top-0 z-40 w-64 shadow-none"
          : isOpen
            ? "top-0 z-[9999] w-72 translate-x-0"
            : isArabic
              ? "translate-x-full w-72"
              : "-translate-x-full w-72"
      )}
    >
      <div
        className={clsx(
          "flex items-center justify-between px-6 h-[72px] border-b transition-all duration-300",
          isDark ? "border-[#1E293B]" : "border-gray-100"
        )}
      >
        <h1
          className={clsx(
            "font-black text-[#7400BF] tracking-tighter truncate",
            "text-xl"
          )}
        >
          {storeName}
        </h1>

        {!isAlwaysVisible && (
          <button
            onClick={onClose}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors
              ${isDark ? " text-gray-400" : " text-gray-500"}`}
          >
            <FiChevronRight className={isArabic ? "" : "rotate-180"} size={20} />
          </button>
        )}
      </div>

      <nav
        className="flex-1 p-4 overflow-y-auto space-y-1"
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {menu.map((item) => {
          const hasSub = !!item.sub;
          const isOpenMenu = openMenu === item.key;
          const isSubActive = hasSub && item.sub.some(sub => location.pathname === sub.to);
          const isActive = (item.to && location.pathname === item.to) || isSubActive;

          return (
            <div key={item.key}>
              <button
                onClick={() => {
                  if (hasSub) setOpenMenu(isOpenMenu ? "" : item.key);
                  else if (item.to) {
                    navigateWithExpiredPlanGuard(navigate, item.to, { isArabic, isDark });
                  }
                }}
                className={clsx(
                  "w-full flex items-center justify-between py-2.5 px-3 rounded-xl transition-all duration-200 text-sm",
                  isActive ? "bg-[#7400BF]/10 font-bold" : "font-medium",
                  isActive
                    ? (isDark ? "text-[#7400BF]" : "text-[#7400BF]")
                    : (isDark ? "text-gray-200" : "text-gray-700")
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={clsx(
                    "w-9 h-9 flex items-center justify-center rounded-lg transition-colors",
                    isActive ? "bg-[#7400BF]/20 text-[#7400BF]" : "bg-[#7400BF]/10 text-[#7400BF]"
                  )}>
                    {item.icon}
                  </div>
                  {item.key === "notifications" && !isArabic ? (
                    <span className="flex flex-col leading-tight">
                      <span>Add Review</span>
                      <span>/ Notification</span>
                    </span>
                  ) : (
                    <span>{item.label}</span>
                  )}
                </div>
                {hasSub && (
                  <span
                    className={`transition-transform ${isOpenMenu ? "rotate-90" : ""
                      }`}
                  >
                    <FiChevronRight />
                  </span>
                )}
              </button>

              {hasSub && isOpenMenu && (
                <div
                  className={clsx(
                    "mt-1 space-y-1",
                    isArabic ? "pr-6 border-r-2" : "pl-6 border-l-2",
                    "border-[#7400BF]/30"
                  )}
                >
                  {item.sub.map((sub) => (
                    <NavLink
                      key={sub.key}
                      to={sub.to}
                      onClick={(event) => {
                        event.preventDefault();
                        navigateWithExpiredPlanGuard(navigate, sub.to, { isArabic, isDark });
                      }}
                      className={({ isActive: isCurrentlyActive }) =>
                        clsx(
                          "block py-1.5 px-2 rounded-md text-sm transition",
                          isCurrentlyActive
                            ? "text-[#7400BF] font-bold bg-[#7400BF]/5"
                            : isDark ? "text-gray-400 hover:text-gray-200" : "text-gray-600 hover:text-gray-900"
                        )
                      }
                    >
                      {sub.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
